import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSettings } from '../../context/SettingsContext';
import { useI18n } from '../../context/I18nContext';
import { MAX_AUTO_LOGOUT_MINUTES } from '../../constants/autoLogout';
import './AutoLogoutPage.css';

const AutoLogoutPage = ({ onBack }) => {
  const { settings, updateSetting } = useSettings();
  const { t } = useI18n();

  // Track the saved value separately to ensure proper comparison after saves
  const savedValueRef = useRef(settings.autoLogoutTimer ?? 0);
  // Track success message timeout for cleanup
  const successTimeoutRef = useRef(null);

  const [logoutTime, setLogoutTime] = useState(() => {
    // Load initial value from settings context
    return String(settings.autoLogoutTimer ?? 0);
  });

  const [showSuccess, setShowSuccess] = useState(false);

  // Sync with external settings changes (e.g., from another tab)
  useEffect(() => {
    const newSavedValue = settings.autoLogoutTimer ?? 0;
    savedValueRef.current = newSavedValue;
    setLogoutTime(String(newSavedValue));
  }, [settings.autoLogoutTimer]);

  // Cleanup success message timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const handleKeyDown = (e) => {
    // Prevent: minus, plus, period, 'e' (exponential notation)
    if (['-', '+', '.', 'e', 'E'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleTimeChange = (e) => {
    let value = e.target.value;
    
    // Strip out any non-digit characters (including -, +, e, .)
    value = value.replace(/[^\d]/g, '');
    
    // Allow empty string for easier editing
    if (value === '') {
      setLogoutTime('');
      return;
    }
    
    // Parse to remove leading zeros
    const numValue = parseInt(value, 10);
    
    // Reject negative numbers (extra safety)
    if (numValue < 0) {
      return;
    }
    
    // Cap at maximum value
    if (numValue > MAX_AUTO_LOGOUT_MINUTES) {
      setLogoutTime(String(MAX_AUTO_LOGOUT_MINUTES));
    } else {
      // Convert back to string without leading zeros
      setLogoutTime(String(numValue));
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    // If the input is empty, save it as 0.
    const timeAsNumber = parseInt(logoutTime, 10) || 0;
    
    // Clear the last activity timestamp to reset the inactivity tracking
    // The timer will be reset via the 'settings-changed' event in useAutoLogout
    try {
      localStorage.removeItem('ti_last_activity_timestamp');
    } catch {
      // Ignore errors - localStorage may not be available
    }
    
    updateSetting('autoLogoutTimer', timeAsNumber);
    // Update the saved value reference so hasChanges becomes false
    savedValueRef.current = timeAsNumber;
    
    // Clear any existing success timeout
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }

    // Show success message
    setShowSuccess(true);

    // Hide success message after 3 seconds
    successTimeoutRef.current = setTimeout(() => {
      setShowSuccess(false);
      successTimeoutRef.current = null;
    }, 3000);
  };

  const handleBack = () => {
    // Revert any unsaved changes by reloading from the saved value
    setLogoutTime(String(savedValueRef.current));
    onBack();
  };

  // Check if the current value differs from the saved setting
  // Compare numeric values to avoid string/number type issues
  const currentValue = parseInt(logoutTime, 10) || 0;
  const hasChanges = currentValue !== savedValueRef.current;

  return (
    <div className="auto-logout-page">
      <div className="settings-modal-header">
        <button className="back-button" onClick={handleBack} aria-label={t('AutoLogoutPage.goBackToSettings')}>
          <ion-icon name="chevron-back-outline" />
        </button>
        <span className="settings-page-title">{t('AutoLogoutPage.title')}</span>
      </div>
      <div className="auto-logout-content">
        <form className="auto-logout-form" onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="logout-timer-input" className="form-label">
              {t('AutoLogoutPage.description')}
            </label>
            <input
              id="logout-timer-input"
              type="number"
              min="0"
              max={MAX_AUTO_LOGOUT_MINUTES}
              className="form-input"
              value={logoutTime}
              onChange={handleTimeChange}
              onKeyDown={handleKeyDown}
              autoComplete="off"
            />
            {showSuccess && (
              <div
                className="success-message"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                ✓ {t('AutoLogoutPage.settingsSaved')}
              </div>
            )}
          </div>
          <div className="auto-logout-footer">
            <button type="submit" className="settings-modal-button primary" disabled={!hasChanges}>
              {t('AutoLogoutPage.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

AutoLogoutPage.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default AutoLogoutPage;